import type { IOfficeHost } from "@core/shared/office-hosts/office-host.types";
import { OutlookHost } from "@core/shared/office-hosts/outlook-host";

/**
 * The narrowest stable id of the open document — in Outlook that is the single
 * mail item, not the conversation it belongs to.
 *
 * getFirstMessageIdByConversationAsync is temporarily disabled - it resolves to
 * a message id scoped to the calling user's own mailbox, so the same email ends
 * up with a different document id per recipient. internetMessageId (the RFC 5322
 * Message-ID header) is preferred instead: it's set once by the sending server
 * and preserved verbatim in every recipient's copy, so it matches across personal
 * accounts and tenants too - unlike conversationId, which Exchange only
 * guarantees equal within the same tenant. Trade-off: it identifies one message,
 * not a thread, so replies do not auto-consolidate into the original document.
 * conversationId is kept as a fallback for when internetMessageId is missing
 * (e.g. very recently sent items).
 *
 * The AI tab's own thread deliberately does NOT use this - it keys on
 * conversationId so a reply thread stays one conversation (see
 * OfficeDocumentThreadManager.resolveDocumentId).
 */
export async function resolveMessageScopedDocumentId(host: IOfficeHost): Promise<string>
{
    if (host instanceof OutlookHost)
    {
        const internetMessageId = await host.getInternetMessageIdAsync();
        if (internetMessageId)
        {
            return internetMessageId;
        }

        const conversationId = await host.getConversationIdAsync();
        if (conversationId)
        {
            return conversationId;
        }
    }

    return host.getDocumentIDAsync();
}
